<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipTypeList } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { getReinforcePrice, getReinforceRate } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import type { ReinforceRes } from '@/types/smithy'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (item.supremeAttrs) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

function handleChoice(item: EquipItemType | undefined) {
  if (item?.supremeAttrs) return ElMessage.warning('至臻装备不可强化')
  equipItemId.value = item?.id || ''
  isProtect.value = false
}

const reinforceResShow = ref(false)
const isSuc = ref(false)
const reinforceMessage = ref('')
function handleReinforce() {
  if (!equipInfo.value) return
  if (getReinforcePrice(equipInfo.value!.reinforcedLevel) > userStore.gold) {
    return ElMessage.warning('您的金币不足')
  }
  const { id, quality, name } = equipInfo.value
  if (equipInfo.value.reinforcedLevel < 10 || isProtect.value) return reinforce()
  ElMessageBox.confirm(
    `<p class="affix">确定强化<span class="${quality}">${name}</span>吗？【本次强化失败将导致装备强化等级<span class="hp">重置</span>】</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(reinforce)
    .catch(() => {})

  async function reinforce() {
    const {
      isSuc: isReinforceSuc,
      reinforcedLevel,
      message,
      gold,
      userAttr,
    } = await socketEmitAsync<ReinforceRes>({ type: 'reinforce', data: { id, isProtect: isProtect.value }, limit: false })
    isSuc.value = isReinforceSuc
    userStore.userAttr = userAttr

    const item = userStore.itemList.find((item) => item.id === id)
    if (!item || !equipInfo.value) return
    item.reinforcedLevel = reinforcedLevel
    equipInfo.value.reinforcedLevel = reinforcedLevel

    reinforceResShow.value = true
    reinforceMessage.value = message

    userStore.gold = gold

    if (isProtect.value) {
      userStore.consumables.reinforceProtect--
      if (userStore.consumables.reinforceProtect <= 0) isProtect.value = false
    }
  }
}

const isProtect = ref(false)
function beforeProtectChange() {
  if (equipInfo.value!.reinforcedLevel < 8) {
    ElMessage.warning('强化+8以上才可使用强化保护券')
    return false
  }
  if (!userStore.consumables.reinforceProtect) {
    ElMessage.warning('您未拥有强化保护券')
    return false
  }
  return true
}
</script>

<template>
  <div class="reinforce">
    <div class="reinforce-table border-wrap">
      <h4>锻造台</h4>
      <div class="reinforce-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>强化说明</h4>
            <p>1. 强化功能为消耗金币增强装备属性。</p>
            <p>
              2. 每个装备部位的强化都为固定属性，部位强化属性如下：{{ equipIcon.weapon }}攻击；{{ equipIcon.helmet }}破防；{{
                equipIcon.armor
              }}命中；{{ equipIcon.shoes }}攻速；{{ equipIcon.jewelry }}暴击。
            </p>
            <p>3. 强化花费会随着装备强化等级的提升愈加昂贵，请留意金币消耗。</p>
            <p>4. 强化+5 以上的装备时，强化失败会导致装备强化等级<span class="hp">降级</span>。</p>
            <p>5. 强化+8 以上的装备时，强化失败则会导致装备强化等级<span class="hp">降级为 +6</span>。</p>
            <p>6. 强化+10 以上的装备时，强化失败则会导致装备强化等级<span class="hp">重置</span>。</p>
            <p>7. 目前版本中强化+15 达到强化等级上限。</p>
            <p>8. 在<span class="info">继承</span>功能当中，您可以将强化等级转移至其他装备，强化+8及以下时，继承<span class="grow">免费</span>。</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <el-row>
            <el-col :span="12">
              <p>强化等级：{{ equipInfo.reinforcedLevel }}</p>
            </el-col>
            <el-col :span="12">
              <p>强化成功率：{{ getReinforceRate(equipInfo.reinforcedLevel) }}%</p>
            </el-col>
          </el-row>
          <p>强化花费：{{ getReinforcePrice(equipInfo.reinforcedLevel) }} 金币</p>
          <p style="align-items: center; justify-content: flex-start">
            强化保护（{{ userStore.consumables.reinforceProtect }}）：
            <el-switch v-model="isProtect" size="small" :before-change="beforeProtectChange" />
          </p>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleReinforce" :disabled="equipInfo.reinforcedLevel >= 15">
            {{ equipInfo.reinforcedLevel >= 15 ? '已强化满级' : '开始强化' }}
          </el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>背包</h4>
      <p class="cur-gold">💰 {{ formatNumberWithCommas(userStore.gold) }}</p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoice(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>

    <el-dialog v-model="reinforceResShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span :class="isSuc ? 'grow' : 'shrink'">{{ reinforceMessage }}</span>
      </h2>
      <template v-if="equipInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <div style="width: 50%; margin: 0 auto">
              <CommonBtn size="small" style="width: 100%">
                <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
              </CommonBtn>
            </div>
          </template>
          <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 60vh" />
        </el-popover>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="reinforceResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.reinforce {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .reinforce-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-track {
      background-color: rgba($color: #fff, $alpha: 0.1);
    }
    &::-webkit-scrollbar-thumb {
      background-color: #b9b9b9;
      border-radius: 5px;
    }
    .reinforce-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
